import {useDispatch, useSelector} from "react-redux";
import React, {useState} from "react";
import DropDownMenuProfile from "../../../../components/DropDown/DropDownMenuProfile";
import {permissionObj} from "../../../../Helpers/permission";
import DropDownMenuItem from "../../../../components/DropDown/DropDownMenuItem";
import {
    CheckIconsOptions,
    CrossIconsOptions,
    DeleteIconsOptions,
    DetailsIconsOptions,
    PlusIconsOptions,
    UpdateIconsOptions
} from "../../../../components/buttons/IconButtons";
import {SimpleSearch} from "../../../../reduxToolkit/Search/SearchApi";
import routes from "../../../../Routes/routes";
import CustomChip from "../../../../components/CustomChip";
import {statusBorderColor, statusColor, statusFontColor} from "../../../../enums/statusColorEnum";
import {status, STATUS_MODEL} from "../../../../enums/statusEnum";
import {EMPTY_VALUES} from "../../../../Apis/functions";
import TableFunction from "../../../../components/table/TableFunction";
import {
    LEFT_FIRST_COLUMN_WITH_CHECKBOX,
    SEARCH_MODEL
} from "../../../../components/table/Model/TableModel";
import { useTranslation } from "react-i18next";

/**
 * @email azapata@ibl.mx
 * @author Ferro Zapata Arnulfo
 * @jobTitle Full Stack Developer
 *
 * TransportRequestTableApp component is the main module for handling transport request logic,
 * rendering table UI, managing modal dialogs for actions (accept, decline, comment, etc.),
 * handling search and pagination, and interacting with Redux state and backend APIs.
 *
 * Features:
 * - Displays a paginated, searchable table of transport requests.
 * - Renders dynamic action menus based on user roles and request statuses.
 * - Handles various user interactions such as accepting, rejecting, commenting, deleting.
 * - Shows expandable rows with detailed transport request information.
 * - Interacts with Redux for fetching, updating, and deleting data.
 *
 * State:
 * - Controls modals for accept/decline/assign/comment/detail.
 * - Manages search/filter and pagination states.
 * - Stores selected row data and expansion state.
 *
 * Hooks:
 * - useDispatch: dispatches Redux actions.
 * - useSelector: accesses Redux store state.
 * - useNavigate: navigates between routes.
 * - useEffect: handles lifecycle-based data loading and updates.
 *
 * Permissions:
 * - Menu options conditionally rendered based on supplier presence and user permissions.
 *
 * Dependencies:
 * - Redux slices: authentication, commons, search
 * - Constants: STATUS_MODEL, TABLES, MODELS, permissionObj
 * - UI Components: DropDownMenuProfile, DropDownMenuItem, CustomChip, etc.
 */

export const TransportRequestTableApp =({
    option,
    modified,
    setModified,
    setBodyForExport,
    selectedList,
    setSelectedList,
    searchElement,
    setIsAllChecked,
    setShowAcceptRequest,
    setRow,
    setShowDeclineRequest,
    setShowAssignEmployeesAndVehicles,
    setShowPreDepartureComment,
    setShowInTransitIssueComment,
    setShowIssueComment,
    setShowDetails,
    setShowDepartureComment,
    setShowArrivalComment,
    setShowDeleteModal
}) => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const supplierId = sessionStorage.getItem('providerId');
    const [searchResults, setSearchResults] = useState('');
    const [expandedRowId, setExpandedRowId] = useState(null);
    const searchId = 'txt_trta_search';
    const DEV = useSelector((state) => state?.CommonsSlice?.development);
    const { permission } = useSelector((state) => state.authenticatioauthennSlice);

    /**
     * Executes a paginated search request using basic criteria.
     *
     * Builds the request body with the current page, page size, selected model,
     * and any additional criteria passed as arguments.
     * Triggers the search action through Redux and updates the results on success.
     */
    const getData = page => {
        const body = SEARCH_MODEL.setObject({page, size:rowsPerPage, model:option, key:searchElement })
        setBodyForExport(body);
        dispatch(SimpleSearch({ body })).then(
            ({payload: {data: { data, success }}}) => {
                if (success === true) {
                    setSearchResults(data);
                }
            },
        );
    }

    /**
     * Prepares table rows by disabling the checkbox for records that are in transit.
     *
     * Rows with status IN_TRANSIT:
     * This is required to prevent invalid operations on transport requests
     * that are currently in progress.
     */

    const rowsWithDisabled = (searchResults?.content || []).map((row) => ({
        ...row,
        disabled: row.statusId === STATUS_MODEL.IN_TRANSIT,
        disabledCheckboxText: row.statusId === STATUS_MODEL.IN_TRANSIT ? t('cannot_delete_in_transit') : '',
    }));

    const { handleCheckboxChange, handleDeleteAll, openMenu, handleMenuToggle, handleClose,
        pagination, rowsPerPage} = TableFunction({
        selectedList,
        setSelectedList,
         data: { ...searchResults, content: rowsWithDisabled },
        labelRowsPerPage:'transport_request_per_page',
        fixedPagination:true,
        hasContainer:false,
        setIsAllChecked,
        searchElement, //New
        getData,//New
        modified,//New
        setModified,//New
    });

    const renderOptionsMenu = (row) => {
        return (
            <>
                <DropDownMenuProfile openMenu={openMenu} handleMenuToggle={handleMenuToggle}
                                     menuId={`menu-${row.id}`} isAccordion={true}>
                    {supplierId !== null ?
                        <>
                            {(row.statusId === STATUS_MODEL.REQUESTED ||
                                row.statusId === STATUS_MODEL.ASSIGNED) && (
                                <DropDownMenuItem
                                    index={row.id}
                                    handleClose={handleClose}
                                    name={t('accept')}
                                    click={() => {
                                        setShowAcceptRequest(true);
                                        setRow(row)
                                    }}
                                    icon={<CheckIconsOptions/>}
                                />)
                            }
                            {row.statusId === STATUS_MODEL.ASSIGNED && (
                                    <DropDownMenuItem
                                        index={row.id}
                                        handleClose={handleClose}
                                        name={t('decline')}
                                        click={() => {
                                            setRow(row)
                                            setShowDeclineRequest(true);
                                        }}
                                        icon={<CrossIconsOptions/>}
                                    />)
                            }
                            {row.statusId === STATUS_MODEL.ACCEPTED && (
                                <DropDownMenuItem
                                    index={row.id}
                                    handleClose={handleClose}
                                    name={t('assign_employee_vehicle')}
                                    click={() => {
                                        setShowAssignEmployeesAndVehicles(true);
                                        setRow(row)
                                    }}
                                    icon={<CheckIconsOptions/>}
                                />)
                            }
                            {row.statusId === STATUS_MODEL.ACCEPTED && (
                                <DropDownMenuItem
                                    index={row.id}
                                    handleClose={handleClose}
                                    name={t('add_pre_departure_comment')}
                                    click={() => {
                                        setShowPreDepartureComment(true);
                                        setRow(row)
                                    }}
                                    icon={<PlusIconsOptions className="icon-color-general-950"/>}
                                />)
                            }
                            {row.statusId === STATUS_MODEL.IN_TRANSIT && (
                                <DropDownMenuItem
                                    index={row.id}
                                    handleClose={handleClose}
                                    name={t('add_issue_comment')}
                                    click={() => {
                                        setRow(row)
                                        setShowInTransitIssueComment(true);
                                    }}
                                    icon={<PlusIconsOptions className="icon-color-general-950" />}
                                />
                            )}
                            {(  row.statusId === STATUS_MODEL.ACCEPTED ||
                                row.statusId === STATUS_MODEL.IN_TRANSIT) && (
                                    <DropDownMenuItem
                                        index={row.id}
                                        handleClose={handleClose}
                                        name={t('cancel')}
                                        click={() => {
                                            setRow(row)
                                            setShowIssueComment(true);
                                        }}
                                        icon={<CrossIconsOptions/>}
                                    />
                                )
                            }
                            {(  row.statusId === STATUS_MODEL.DELIVERED ||
                                row.statusId === STATUS_MODEL.CANCELED ||
                                row.statusId === STATUS_MODEL.REJECTED) && (
                                <DropDownMenuItem
                                    index={row.id}
                                    handleClose={handleClose}
                                    name={t('details')}
                                    click={() => {
                                        setShowDetails(true);
                                        setRow(row)
                                    }}
                                    icon={<DetailsIconsOptions/>}
                                />)
                            }
                        </> :
                        <>
                            {(row.statusId === STATUS_MODEL.REQUESTED ||
                                row.statusId === STATUS_MODEL.ASSIGNED) &&
                                (DEV || permission?.includes(permissionObj.WEB_SERVICE_TYPE_UPDATE)) && (
                                    <DropDownMenuItem
                                        index={row.id}
                                        handleClose={handleClose}
                                        name={t('update')}
                                        url={`${routes.transportRequest.update}${row.id}`}
                                        icon={<UpdateIconsOptions/>}
                                    />
                            )}
                            {(row.statusId === STATUS_MODEL.ACCEPTED &&
                                row?.SupplierEmployeeId !== null &&
                                row?.supplierVehicleId !== null) && (
                                <DropDownMenuItem
                                    index={row.id}
                                    handleClose={handleClose}
                                    name={t('add_departure_comment')}
                                    click={() => {
                                        setShowDepartureComment(true);
                                        setRow(row);
                                    }}
                                    icon={<PlusIconsOptions className="icon-color-general-950" />}
                                />
                            )}
                            {row.statusId === STATUS_MODEL.IN_TRANSIT && (
                                <DropDownMenuItem
                                    index={row.id}
                                    handleClose={handleClose}
                                    name={t('add_arrival_comment')}
                                    click={() => {
                                        setRow(row)
                                        setShowArrivalComment(true);
                                    }}
                                    icon={<PlusIconsOptions className="icon-color-general-950" />}
                                />
                            )}
                            {(  row.statusId === STATUS_MODEL.DELIVERED ||
                                row.statusId === STATUS_MODEL.CANCELED ||
                                row.statusId === STATUS_MODEL.REJECTED) && (
                                <DropDownMenuItem
                                    index={row.id}
                                    handleClose={handleClose}
                                    name={t('details')}
                                    click={() => {
                                        setShowDetails(true);
                                        setRow(row)
                                    }}
                                    icon={<DetailsIconsOptions/>}
                                />)
                            }
                            {(  row.statusId === STATUS_MODEL.ACCEPTED||
                                row.statusId === STATUS_MODEL.IN_TRANSIT) && (
                                <DropDownMenuItem
                                    index={row.id}
                                    handleClose={handleClose}
                                    name={t('cancel')}
                                    click={() => {
                                        setRow(row)
                                        setShowIssueComment(true);
                                    }}
                                    icon={<CrossIconsOptions/>}
                                />
                            )}
                            {((DEV || permission?.includes(permissionObj?.WEB_SERVICE_TYPE_DELETE))&&
                                (   row.statusId === STATUS_MODEL.REQUESTED ||
                                    row.statusId === STATUS_MODEL.ASSIGNED ||
                                    row.statusId === STATUS_MODEL.DELIVERED ||
                                    row.statusId === STATUS_MODEL.CANCELED ||
                                    row.statusId === STATUS_MODEL.REJECTED)) && (
                                <DropDownMenuItem
                                    index={row.id}
                                    handleClose={handleClose}
                                    name={t('delete')}
                                    click={() => {
                                        setSelectedList([row.id])
                                        setShowDeleteModal(true);
                                    }}
                                    icon={<DeleteIconsOptions />}
                                    fontColor='font-color-error-600'
                                    iconColor={"icon-color-error-code-600"}
                                />
                            )}
                        </>
                    }
                </DropDownMenuProfile>
            </>
        )
    };

    const handleRowExpand = (row) => {
        if (row?.id !== expandedRowId) {
            setExpandedRowId(row?.id);
        }
    };

    const columns = [
        { field: 'item', label: 'item', fixed: true, left: LEFT_FIRST_COLUMN_WITH_CHECKBOX },
        { field: 'originCenterName', label: 'origin_center_name' },
        { field: 'destinationCenterName', label: 'destination_center_name' },
        { field: 'departureAt', label: 'pickup_date' },
        { field: 'deliveryAt', label: 'delivery_date' },
        {
            field: 'statusId', label: 'status',
            render: (value) => {
                return (
                    <CustomChip
                        background={statusColor[value]}
                        fontColor={statusFontColor[value]}
                        borderColor={statusBorderColor[value]}
                        legend={t(status[value]) || EMPTY_VALUES.DEFAULT} variant={'large'}
                    />
                );
            }
        },
    ];





    return {
        DEV,
        permission,
        searchId,
        rowsPerPage,
        searchResults,
        setSearchResults,
        handleCheckboxChange,
        pagination,
        handleDeleteAll,
        renderOptionsMenu,
        handleRowExpand,
        columns,
        t,
        rowsWithDisabled,
    };
}
